import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../utils/api";
import { useApp } from "../context/AppContext";
import { t } from "../utils/i18n";
import { SharedGardenIcon, WishIcon, DreamIcon, MemoryIcon } from "../components/GardenIcons";
import FriendSelector from "../components/FriendSelector";
import { GARDEN_QUESTIONS } from "../utils/gardenQuestions";

// Notre Jardin — espace partagé. Calqué sur venue-members (invite → pending → accept/decline),
// jamais de join libre. Une seule page : liste de mes jardins (créer si aucun), puis le détail du
// premier jardin actif (membres, contenu partagé, Questions à deux).
export default function NotreJardin() {
  const navigate = useNavigate();
  const { lang, user } = useApp();
  const [gardens, setGardens] = useState<any[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [members, setMembers] = useState<any[]>([]);
  const [items, setItems] = useState<any[]>([]);
  const [showInvite, setShowInvite] = useState(false);
  const [inviting, setInviting] = useState<string[]>([]);
  const [instances, setInstances] = useState<any[]>([]);
  const [showAsk, setShowAsk] = useState(false);

  const loadGardens = () => {
    api.get("/garden/gardens/mine").then(d => {
      const list = d?.gardens || [];
      setGardens(list);
      const active = list.find((g: any) => g.my_status === "active");
      if (active && !selectedId) setSelectedId(active.id);
    }).catch(() => setGardens([]));
  };
  useEffect(loadGardens, []);

  const loadDetail = (id: string) => {
    api.get(`/garden/gardens/${id}/members`).then(d => setMembers(d?.members || []));
    api.get(`/garden/gardens/${id}/items`).then(d => setItems([...(d?.items || []), ...(d?.desires || [])]));
  };
  useEffect(() => { if (selectedId) loadDetail(selectedId); }, [selectedId]);

  const selected = gardens?.find(g => g.id === selectedId);

  const createGarden = async () => {
    const data = await api.post("/garden/gardens", {});
    if (data?.garden) { loadGardens(); setSelectedId(data.garden.id); }
  };

  const respond = async (gardenId: string, accept: boolean) => {
    await api.put(`/garden/gardens/${gardenId}/members/${user!.id}/${accept ? "accept" : "decline"}`, {});
    loadGardens();
  };

  const sendInvites = async () => {
    if (!selectedId) return;
    await Promise.all(inviting.map(id => api.post(`/garden/gardens/${selectedId}/invite`, { user_id: id })));
    setShowInvite(false); setInviting([]);
    loadDetail(selectedId);
  };

  const askQuestion = async (key: string) => {
    if (!selectedId) return;
    const data = await api.post(`/garden/gardens/${selectedId}/questions`, { question_key: key });
    if (data?.instance) setInstances(prev => [data.instance, ...prev]);
    setShowAsk(false);
  };

  const pending = (gardens || []).filter(g => g.my_status === "pending");

  return (
    <div style={{ maxWidth: 560, margin: "0 auto", padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <button onClick={() => navigate("/garden")} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#666" }}>←</button>
        <SharedGardenIcon size={18} color="#0a0a0a" />
        <span style={{ fontSize: 20, fontWeight: 700 }}>{t(lang, "garden_notre_jardin_title")}</span>
      </div>

      {pending.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          {pending.map(g => (
            <div key={g.id} style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid #F0F0F0", borderRadius: 14, padding: 14, marginBottom: 8 }}>
              <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{g.name}</span>
              <button onClick={() => respond(g.id, true)} style={{ border: "none", borderRadius: 20, padding: "7px 14px", backgroundColor: "#0a0a0a", color: "white", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>✓</button>
              <button onClick={() => respond(g.id, false)} style={{ border: "1px solid #E0E0E0", borderRadius: 20, padding: "7px 14px", backgroundColor: "white", color: "#666", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>✕</button>
            </div>
          ))}
        </div>
      )}

      {gardens === null ? (
        <div style={{ textAlign: "center", color: "#999", marginTop: 40 }}>{t(lang, "msg_loading")}</div>
      ) : !selected ? (
        <div style={{ textAlign: "center", marginTop: 40 }}>
          <div style={{ fontSize: 13, color: "#999", marginBottom: 16 }}>{t(lang, "garden_notre_jardin_empty")}</div>
          <button onClick={createGarden} style={{ padding: "12px 24px", borderRadius: 24, border: "none", backgroundColor: "#0a0a0a", color: "white", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
            {t(lang, "garden_notre_jardin_create")}
          </button>
        </div>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div style={{ display: "flex", gap: -8 }}>
              {members.map(m => (
                <div key={m.id} title={m.name} style={{ width: 32, height: 32, borderRadius: 16, overflow: "hidden", backgroundColor: "#0a0a0a", border: "2px solid white", marginLeft: -8, display: "flex", alignItems: "center", justifyContent: "center", opacity: m.status === "pending" ? 0.4 : 1 }}>
                  {m.avatar_url ? <img src={m.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ color: "white", fontSize: 11, fontWeight: 700 }}>{(m.name || "?")[0]}</span>}
                </div>
              ))}
            </div>
            <button onClick={() => setShowInvite(true)} style={{ border: "1px solid #E0E0E0", borderRadius: 20, padding: "8px 16px", backgroundColor: "white", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
              {t(lang, "garden_invite_cta")}
            </button>
          </div>

          {showInvite && (
            <div style={{ border: "1px solid #EEE", borderRadius: 14, padding: 14, marginBottom: 16 }}>
              <FriendSelector selected={inviting} onToggle={id => setInviting(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id])} excludeIds={members.map(m => m.id)} />
              <button onClick={sendInvites} disabled={inviting.length === 0} style={{ width: "100%", padding: 10, borderRadius: 12, border: "none", backgroundColor: "#0a0a0a", color: "white", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                {t(lang, "garden_invite_cta")}
              </button>
            </div>
          )}

          {/* Questions à deux */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#666", textTransform: "uppercase", letterSpacing: 0.5 }}>{t(lang, "garden_question_ask")}</span>
              <button onClick={() => setShowAsk(v => !v)} style={{ border: "none", background: "none", fontSize: 20, cursor: "pointer" }}>{showAsk ? "✕" : "+"}</button>
            </div>
            {showAsk && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
                {GARDEN_QUESTIONS.map(q => (
                  <button key={q.key} onClick={() => askQuestion(q.key)} style={{ textAlign: "left", padding: "10px 14px", borderRadius: 12, border: "1px solid #E0E0E0", backgroundColor: "white", fontSize: 13, cursor: "pointer" }}>
                    {lang === "fr" ? q.labelFr : q.labelEn}
                  </button>
                ))}
              </div>
            )}
            {instances.map(inst => <QuestionCard key={inst.id} gardenId={selectedId!} instance={inst} lang={lang} />)}
          </div>

          {/* Contenu partagé */}
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#666", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>{t(lang, "garden_notre_jardin_title")}</div>
            {items.length === 0 ? (
              <div style={{ fontSize: 13, color: "#999" }}>{t(lang, "garden_empty")}</div>
            ) : items.map((it, i) => (
              <div key={it.id || i} style={{ display: "flex", alignItems: "center", gap: 12, border: "1px solid #EEE", borderRadius: 14, padding: 12, marginBottom: 10 }}>
                <div style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: "#F5F5F5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {it.category === "dream" ? <DreamIcon size={17} /> : it.category === "memory" ? <MemoryIcon size={17} /> : <WishIcon size={17} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700 }}>{it.title || "—"}</div>
                  <div style={{ fontSize: 11.5, color: "#999" }}>{it.author_name}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function QuestionCard({ gardenId, instance, lang }: { gardenId: string; instance: any; lang: string }) {
  const [state, setState] = useState<any>(null);
  const [answer, setAnswer] = useState("");
  const [sent, setSent] = useState(false);

  const load = () => api.get(`/garden/gardens/${gardenId}/questions/${instance.id}`).then(setState);
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!answer.trim()) return;
    await api.post(`/garden/gardens/${gardenId}/questions/${instance.id}/answer`, { answer: answer.trim() });
    setSent(true); load();
  };

  const q = GARDEN_QUESTIONS.find(q => q.key === instance.question_key);
  if (!state) return null;

  return (
    <div style={{ border: "1px solid #EEE", borderRadius: 14, padding: 14, marginBottom: 10 }}>
      <div style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 10 }}>{q ? (lang === "fr" ? q.labelFr : q.labelEn) : instance.question_key}</div>
      {state.revealed ? (
        state.answers.map((a: any) => (
          <div key={a.user_id} style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#999" }}>{a.name}</div>
            <div style={{ fontSize: 13 }}>{a.answer}</div>
          </div>
        ))
      ) : state.answered_by_me || sent ? (
        <div style={{ fontSize: 12.5, color: "#999", fontStyle: "italic" }}>{t(lang, "garden_question_waiting_other")}</div>
      ) : (
        <div style={{ display: "flex", gap: 8 }}>
          <input value={answer} onChange={e => setAnswer(e.target.value)} placeholder={t(lang, "garden_question_placeholder")} style={{ flex: 1, padding: "9px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 13 }} />
          <button onClick={submit} style={{ border: "none", borderRadius: 10, padding: "9px 14px", backgroundColor: "#0a0a0a", color: "white", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>→</button>
        </div>
      )}
    </div>
  );
}
